import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./css/PaymentPage.css";
import {
  getProductPresentation,
  SELLER_INN,
  SELLER_NAME,
  SELLER_STATUS,
  SUPPORT_EMAIL,
  TELEGRAM_SUPPORT_HANDLE,
  TELEGRAM_SUPPORT_URL,
  VK_OFFER_URL
} from "../content/productContent";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.chatgpt.sverhum.ru";
const PAYMENT_HUB_URL = process.env.REACT_APP_PAYMENT_HUB_URL || "https://sverhum.ru";

const paymentMethodTitles = {
  yookassa: "Любой способ в YooKassa",
  yookassa_card: "Банковская карта",
  yookassa_sbp: "СБП"
};
const defaultProductOptions = Array.from({ length: 14 }, (_, index) => {
  const productId = index + 1;
  const presentation = getProductPresentation(productId);
  return {
    id: productId,
    label: presentation.title
  };
});

const formatter = new Intl.NumberFormat("ru-RU");

function formatRub(value) {
  return `${formatter.format(Number(value || 0))} ₽`;
}

function formatDateTime(value) {
  const date = new Date(Number(value || 0) * 1000);
  if (Number.isNaN(date.getTime())) return "Не указано";

  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

export default function PaymentPage() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = String(query.get("token") || "").trim();
  const methodFromQuery = String(query.get("method") || "yookassa").trim().toLowerCase();
  const [paymentMethod, setPaymentMethod] = useState(
    paymentMethodTitles[methodFromQuery] ? methodFromQuery : "yookassa"
  );
  const [vkUserId, setVkUserId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(1);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(token ? "loading" : "idle");

  useEffect(() => {
    if (!token) {
      setStatus("idle");
      setPreview(null);
      return undefined;
    }

    const controller = new AbortController();

    async function loadPreview() {
      try {
        setStatus("loading");

        const response = await fetch(
          `${API_BASE_URL}/payment-preview?token=${encodeURIComponent(token)}`,
          { signal: controller.signal }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Payment preview failed");
        }

        setPreview(payload);
        setStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        setStatus("error");
        setPreview(null);
      }
    }

    loadPreview();
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!preview?.availableMethods?.length) return;
    if (preview.availableMethods.includes(paymentMethod)) return;
    setPaymentMethod(preview.availableMethods[0]);
  }, [paymentMethod, preview]);

  const presentation = useMemo(() => {
    if (preview?.productId) {
      return getProductPresentation(preview.productId, preview.title);
    }
    return getProductPresentation(selectedProductId);
  }, [preview?.productId, preview?.title, selectedProductId]);

  const paymentUrl = useMemo(() => {
    if (!token) return "";
    const params = new URLSearchParams();
    params.set("bot", "chatgpt");
    params.set("product", String(preview?.productId || selectedProductId || 1));
    params.set("context", token);
    if (paymentMethod) params.set("method", paymentMethod);
    return `${PAYMENT_HUB_URL}/payment?${params.toString()}`;
  }, [paymentMethod, preview?.productId, selectedProductId, token]);

  const availableMethods = preview?.availableMethods?.length
    ? preview.availableMethods
    : Object.keys(paymentMethodTitles);

  const handlePayment = () => {
    if (!paymentUrl) return;
    window.location.assign(paymentUrl);
  };

  const isReady = status === "ready" && preview;
  const showManualForm = !isReady;

  return (
    <main className="payment-page-shell">
      <section className="payment-layout">
        <div className="payment-primary">
          <div className="payment-brand-row">
            <div>
              <div className="payment-eyebrow">Оплата через YooKassa</div>
              <h1 className="payment-title">Оплата подписки</h1>
            </div>
            <img className="payment-logo" src="/ЮKassa_logo.svg" alt="YooKassa" />
          </div>

          <p className="payment-description">
            На этой странице отображаются данные заказа, сумма и способ перехода к платёжной
            форме YooKassa. Если параметры покупки уже переданы, они загрузятся автоматически.
          </p>

          {status === "loading" && (
            <section className="payment-panel payment-panel-muted">
              <div className="payment-panel-title">Загружаем данные оплаты</div>
              <p>Загружаем данные заказа и доступные способы оплаты.</p>
            </section>
          )}

          {showManualForm ? (
            <section className="payment-card">
              <div className="payment-summary-head">
                <div>
                  <div className="payment-chip">{presentation.typeLabel}</div>
                  <h2>{presentation.title}</h2>
                  <p>{presentation.summary}</p>
                </div>
              </div>

              <div className="payment-order-grid">
                <div className="payment-order-info">
                  <dl className="payment-facts">
                    <div>
                      <dt>Товар</dt>
                      <dd>{presentation.title}</dd>
                    </div>
                    <div>
                      <dt>Формат</dt>
                      <dd>{presentation.typeLabel}</dd>
                    </div>
                    <div>
                      <dt>Начисление после оплаты</dt>
                      <dd>{presentation.delivery}</dd>
                    </div>
                  </dl>

                  <div className="payment-feature-box">
                    <h3>Что входит в покупку</h3>
                    <ul>
                      {presentation.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="payment-checkout-box">
                  <label className="payment-label" htmlFor="vkUserId">
                    ID из VK
                  </label>
                  <input
                    id="vkUserId"
                    className="payment-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="Например, 123456789"
                    value={vkUserId}
                    onChange={(event) => setVkUserId(event.target.value)}
                  />

                  <label className="payment-label" htmlFor="productId">
                    Продукт
                  </label>
                  <select
                    id="productId"
                    className="payment-select"
                    value={selectedProductId}
                    onChange={(event) => setSelectedProductId(Number(event.target.value))}
                  >
                    {defaultProductOptions.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.label}
                      </option>
                    ))}
                  </select>

                  <label className="payment-label" htmlFor="paymentMethod">
                    Способ оплаты
                  </label>
                  <select
                    id="paymentMethod"
                    className="payment-select"
                    value={paymentMethod}
                    onChange={(event) => setPaymentMethod(event.target.value)}
                  >
                    {availableMethods.map((method) => (
                      <option key={method} value={method}>
                        {paymentMethodTitles[method] || method}
                      </option>
                    ))}
                  </select>

                  <button className="payment-button" type="button">
                    Оплатить
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <section className="payment-card">
              <div className="payment-summary-head">
                <div>
                  <div className="payment-chip">{presentation.typeLabel}</div>
                  <h2>{presentation.title}</h2>
                  <p>{presentation.summary}</p>
                </div>
                <div className="payment-amount-box">
                  <span>К оплате</span>
                  <strong>{isReady ? formatRub(preview.finalAmount) : "Уточняется"}</strong>
                  {isReady && preview.finalAmount !== preview.baseAmount && (
                    <em>Базовая цена: {formatRub(preview.baseAmount)}</em>
                  )}
                </div>
              </div>

              <div className="payment-order-grid">
                <div className="payment-order-info">
                  <dl className="payment-facts">
                    <div>
                      <dt>Товар</dt>
                      <dd>{isReady ? preview.title : presentation.title}</dd>
                    </div>
                    <div>
                      <dt>Формат</dt>
                      <dd>{presentation.typeLabel}</dd>
                    </div>
                    {isReady ? (
                      <div>
                        <dt>Данные заказа актуальны до</dt>
                        <dd>{formatDateTime(preview.expiresAt)}</dd>
                      </div>
                    ) : (
                      <div>
                        <dt>Данные заказа</dt>
                        <dd>Появятся автоматически, когда страница получит параметры покупки.</dd>
                      </div>
                    )}
                    <div>
                      <dt>Начисление после оплаты</dt>
                      <dd>{presentation.delivery}</dd>
                    </div>
                  </dl>

                  <div className="payment-feature-box">
                    <h3>Что входит в покупку</h3>
                    <ul>
                      {presentation.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="payment-checkout-box">
                  <label className="payment-label" htmlFor="paymentMethod">
                    Способ оплаты
                  </label>
                  <select
                    id="paymentMethod"
                    className="payment-select"
                    value={paymentMethod}
                    onChange={(event) => setPaymentMethod(event.target.value)}
                  >
                    {availableMethods.map((method) => (
                      <option key={method} value={method}>
                        {paymentMethodTitles[method] || method}
                      </option>
                    ))}
                  </select>

                  <button className="payment-button" onClick={handlePayment}>
                    Оплатить
                  </button>

                  <p className="payment-button-note">
                    После нажатия откроется форма YooKassa. Возврат на сайт ведёт на страницу
                    подтверждения оплаты.
                  </p>

                  {isReady && preview.discountPercent > 0 && (
                    <div className="payment-inline-note">
                      На этот заказ применена скидка {preview.discountPercent}%.
                    </div>
                  )}

                  {isReady && preview.autoRenew && (
                    <div className="payment-inline-note">
                      Для этого товара используется формат ежемесячной подписки или пакета.
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        <aside className="payment-sidebar">
          <section className="payment-side-card">
            <h2>Как проходит оплата</h2>
            <ol className="payment-steps">
              <li>На странице загружаются состав заказа, сумма и доступные способы оплаты.</li>
              <li>Вы проверяете детали покупки и выбираете удобный способ оплаты.</li>
              <li>Переходите в YooKassa и оплачиваете удобным способом.</li>
              <li>После оплаты доступ начисляется автоматически в рамках выбранного сценария.</li>
            </ol>
          </section>

          <section className="payment-side-card">
            <h2>Поддержка и документы</h2>
            <div className="payment-support-list">
              <p>{SELLER_NAME}</p>
              <p>{SELLER_STATUS}</p>
              <p>ИНН: {SELLER_INN}</p>
              <p>
                Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </p>
              <p>
                Telegram:{" "}
                <a href={TELEGRAM_SUPPORT_URL} target="_blank" rel="noreferrer">
                  {TELEGRAM_SUPPORT_HANDLE}
                </a>
              </p>
              <p>
                Оферта:{" "}
                <a href={VK_OFFER_URL} target="_blank" rel="noreferrer">
                  открыть документ
                </a>
              </p>
            </div>
          </section>
        </aside>
      </section>
    </main>
  );
}
